/**
 * Exchange actions - market buy/sell + ad hook logging
 * Kept separate from actions.ts (file size rule); merged into actionHandlers there.
 */
import type { ActionHandler } from '@app-games/core';
import type { GameState } from './state.js';
import { getCoinConfig } from './config/coins.js';
import { executeBuy, executeSell } from './systems/tradingSystem.js';
import { recordAdHook, type AdHookEvent } from './adHooks.js';
import type { LastTradeState, RiskState } from './riskState.js';
import { onboardingAfterBuy, onboardingAfterSell } from './onboardingState.js';

/** Record the fill for the trade-rewind hook (keeps undo data). */
function withLastTrade(risk: RiskState | undefined, trade: LastTradeState): RiskState | undefined {
  return risk ? { ...risk, lastTrade: trade } : risk;
}

export type ExchangeAction =
  | { type: 'EXCHANGE_BUY'; payload: { coinId: string; spend: number } }
  | { type: 'EXCHANGE_SELL'; payload: { coinId: string; amount: number } }
  | { type: 'AD_HOOK_LOG'; payload: { hookId: string; event: AdHookEvent } };

/**
 * Market buy: spend game coins (mining currency) on a virtual coin.
 */
export const handleExchangeBuy: ActionHandler<
  GameState,
  Extract<ExchangeAction, { type: 'EXCHANGE_BUY' }>
> = (state, action) => {
  if (!state.exchange) return null;
  const { coinId, spend } = action.payload;

  if (!getCoinConfig(coinId)) return null;
  const coin = state.exchange.coins[coinId];
  if (!coin) return null;

  const prevHolding = state.exchange.holdings[coinId] ?? null;
  const result = executeBuy(state.coins, prevHolding ?? undefined, coin.price, spend);
  if (!result) return null;

  return {
    ...state,
    coins: result.coins,
    exchange: {
      ...state.exchange,
      holdings: {
        ...state.exchange.holdings,
        [coinId]: result.holding,
      },
      totalTrades: state.exchange.totalTrades + 1,
    },
    risk: withLastTrade(state.risk, {
      coinId, side: 'buy', price: coin.price, amount: result.boughtAmount,
      cash: spend, realizedProfit: 0, prevHolding, at: Date.now(),
    }) as GameState['risk'],
    // Onboarding: buying the scripted target coin kicks off the pump (step 2 -> 3).
    onboarding: onboardingAfterBuy(state.onboarding, coinId, coin.price, Date.now()),
  };
};

/**
 * Market sell: sell held virtual coin back to game coins.
 */
export const handleExchangeSell: ActionHandler<
  GameState,
  Extract<ExchangeAction, { type: 'EXCHANGE_SELL' }>
> = (state, action) => {
  if (!state.exchange) return null;
  const { coinId, amount } = action.payload;

  const coin = state.exchange.coins[coinId];
  if (!coin) return null;

  const prevHolding = state.exchange.holdings[coinId] ?? null;
  const result = executeSell(state.coins, prevHolding ?? undefined, coin.price, amount);
  if (!result) return null;

  const holdings = { ...state.exchange.holdings };
  if (result.holding) {
    holdings[coinId] = result.holding;
  } else {
    delete holdings[coinId];
  }

  return {
    ...state,
    coins: result.coins,
    exchange: {
      ...state.exchange,
      holdings,
      totalTrades: state.exchange.totalTrades + 1,
      realizedProfit: state.exchange.realizedProfit + result.realizedProfit,
    },
    risk: withLastTrade(state.risk, {
      coinId, side: 'sell', price: coin.price, amount,
      cash: result.proceeds, realizedProfit: result.realizedProfit, prevHolding, at: Date.now(),
    }) as GameState['risk'],
    // Onboarding: selling the pumped target coin completes the twist (step 3 -> done).
    onboarding: onboardingAfterSell(state.onboarding, coinId),
  };
};

/**
 * Ad hook funnel logging (shown / accepted).
 */
export const handleAdHookLog: ActionHandler<
  GameState,
  Extract<ExchangeAction, { type: 'AD_HOOK_LOG' }>
> = (state, action) => {
  const { hookId, event } = action.payload;
  if (!hookId) return null;
  return {
    ...state,
    adHookStats: recordAdHook(state.adHookStats ?? {}, hookId, event),
  };
};

export const exchangeActionHandlers = {
  EXCHANGE_BUY: handleExchangeBuy,
  EXCHANGE_SELL: handleExchangeSell,
  AD_HOOK_LOG: handleAdHookLog,
} as const;
